import React, { useState, useEffect, useMemo } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  CalendarIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import { PredictionService, SeasonService, LeagueSeasonService } from '../../services/firebaseService';
import { ContestantService } from '../../services/firebaseService';
import { normalizeToContestantName, getContestantNamesFromIds } from '../../utils/contestantUtils';
import { formatDate } from '../../utils/dateUtils';
import { formatScoreBreakdown } from '../../utils/scoreUtils';
import {
  computeWeekScore,
  hydrateWeekScoreBreakdown,
} from '../../utils/weekScoreEngine';
import { ScoreService } from '../../services/scoreService';

function votedOutOrdinalLabel(position) {
  if (position === 1) return '1st';
  if (position === 2) return '2nd';
  if (position === 3) return '3rd';
  return `${position}th`;
}

/**
 * PredictionResults Component
 * 
 * Reusable component for displaying prediction history with correctness and points.
 * Can be used as a standalone page or embedded in other components.
 * 
 * @param {string} leagueId - League ID
 * @param {string} seasonId - Season ID
 * @param {string} teamId - Team ID
 * @param {boolean} showHeader - Whether to show the header (default: true)
 * @param {boolean} restrictToClosedWeeks - If true, only show predictions for weeks with isOpenForPredictions=false (e.g. when viewing another team's page)
 */
export default function PredictionResults({ leagueId, seasonId, teamId, showHeader = true, restrictToClosedWeeks = false }) {
  const [predictions, setPredictions] = useState([]);
  const [season, setSeason] = useState(null);
  const [leagueSeason, setLeagueSeason] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [scoreBreakdowns, setScoreBreakdowns] = useState({}); // weekNumber -> breakdown
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (leagueId && seasonId && teamId) {
      loadData();
    }
  }, [leagueId, seasonId, teamId]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const { TeamService } = await import('../../services/firebaseService');
      const [
        predictionsData,
        seasonData,
        leagueSeasonData,
        contestantsData,
        team,
        seasonContestantsMap,
      ] = await Promise.all([
        PredictionService.getAllTeamPredictions(leagueId, seasonId, teamId),
        SeasonService.getSeason(seasonId),
        LeagueSeasonService.getLeagueSeason(leagueId, seasonId),
        ContestantService.getContestantsBySeason(seasonId),
        TeamService.getTeam(leagueId, seasonId, teamId),
        ScoreService.getSeasonContestantIdToNameMap(seasonId),
      ]);

      setPredictions(predictionsData);
      setSeason(seasonData);
      setLeagueSeason(leagueSeasonData);
      setContestants(contestantsData);

      const breakdowns = {};
      const weekNumbers = [
        ...new Set(predictionsData.map(p => p.weekNumber).filter(n => n != null)),
      ];

      for (const weekNumber of weekNumbers) {
        const week = seasonData.weeks?.find(w => w.weekNumber === weekNumber);
        if (!week?.results || !team) continue;

        const predsForWeek = predictionsData.filter(
          p => p.weekNumber === weekNumber,
        );
        const key = String(weekNumber);
        const storedWeekScore =
          team.weeklyScores?.[key] ?? team.weeklyScores?.[weekNumber];

        let breakdown = hydrateWeekScoreBreakdown(
          team.weeklyScoreBreakdowns?.[key] ??
            team.weeklyScoreBreakdowns?.[weekNumber],
        );

        if (
          !breakdown ||
          (storedWeekScore != null && breakdown.total !== storedWeekScore)
        ) {
          breakdown = computeWeekScore(
            team,
            predsForWeek,
            week.results,
            weekNumber,
            leagueSeasonData.scoringRules || {},
            seasonData,
            seasonContestantsMap,
            seasonData.mergeWeek ?? null,
          );
        }

        breakdowns[weekNumber] = breakdown;
      }

      setScoreBreakdowns(breakdowns);

    } catch (err) {
      console.error('Error loading prediction results:', err);
      setError(err.message || 'Failed to load prediction results');
    } finally {
      setLoading(false);
    }
  };

  const getWeek = (weekNumber) => {
    return season?.weeks?.find(w => w.weekNumber === weekNumber);
  };

  // When viewing another team's page, only show predictions for closed weeks (isOpenForPredictions === false)
  const displayedPredictions = useMemo(() => {
    if (!restrictToClosedWeeks || !season?.weeks) return predictions;
    return predictions.filter((pred) => {
      const week = season.weeks.find((w) => w.weekNumber === pred.weekNumber);
      return week && week.isOpenForPredictions === false;
    });
  }, [predictions, season, restrictToClosedWeeks]);

  const isPredictionCorrect = (prediction, week) => {
    if (!week || !week.results) return null; // No results yet

    const pred = prediction.predictions || {};
    const results = week.results;

    // Check if double elimination
    const isDoubleElimination = Array.isArray(results.votedOut) && 
      results.votedOut.length > 0 && 
      results.votedOut[0].position;

    if (isDoubleElimination && Array.isArray(pred.votedOut)) {
      // Check each position
      let allCorrect = true;
      for (const predItem of pred.votedOut) {
        if (predItem && predItem.contestantId && predItem.position) {
          const actualResult = results.votedOut.find(r => r.position === predItem.position);
          if (actualResult) {
            // Compare IDs (results may have names, need to convert)
            const actualId = contestants.find(c => c.name === actualResult.contestantName)?.id;
            if (predItem.contestantId !== actualId && predItem.contestantId !== actualResult.contestantId) {
              allCorrect = false;
              break;
            }
          } else {
            allCorrect = false;
            break;
          }
        }
      }
      return allCorrect;
    } else {
      // Single elimination or finale
      let correct = false;

      // Check votedOut
      if (pred.votedOut && results.votedOut) {
        if (Array.isArray(results.votedOut)) {
          const votedOutIds = results.votedOut.map(r => {
            const contestant = contestants.find(c => c.name === (r.contestantName || r));
            return contestant?.id || r;
          });
          if (Array.isArray(pred.votedOut)) {
            correct = pred.votedOut.some(p => votedOutIds.includes(p));
          } else {
            correct = votedOutIds.includes(pred.votedOut);
          }
        } else {
          const resultContestant = contestants.find(c => c.name === results.votedOut || c.id === results.votedOut);
          const predContestant = contestants.find(c => c.id === pred.votedOut);
          correct = resultContestant?.id === predContestant?.id;
        }
      }

      // Check winner (finale)
      if (results.winner && pred.winner) {
        const resultWinner = contestants.find(c => c.name === results.winner || c.id === results.winner);
        const predWinner = contestants.find(c => c.id === pred.winner);
        if (resultWinner?.id === predWinner?.id) {
          correct = true; // At least one correct
        }
      }

      // Check fireMakingLoser (finale)
      if (results.fireMakingLoser && pred.fireMakingLoser) {
        const resultLoser = contestants.find(c => c.name === results.fireMakingLoser || c.id === results.fireMakingLoser);
        const predLoser = contestants.find(c => c.id === pred.fireMakingLoser);
        if (resultLoser?.id === predLoser?.id) {
          correct = true; // At least one correct
        }
      }

      return correct;
    }
  };

  // Helper function to check if a specific prediction type is correct (for finale weeks)
  const isSpecificPredictionCorrect = (prediction, week, predictionType) => {
    if (!week || !week.results) return null;
    
    const pred = prediction.predictions || {};
    const results = week.results;
    
    // Only check finale predictions if this is a finale week
    if (!week.isFinale) return null;
    
    const contestantsArray = contestants || [];
    
    if (predictionType === 'votedOut') {
      if (!pred.votedOut || !results.votedOut) return null;
      const resultContestant = contestantsArray.find(c => c.name === results.votedOut || c.id === results.votedOut);
      const predContestant = contestantsArray.find(c => c.id === pred.votedOut);
      return resultContestant?.id === predContestant?.id;
    }
    
    if (predictionType === 'fireMakingLoser') {
      if (!pred.fireMakingLoser || !results.fireMakingLoser) return null;
      const resultLoser = contestantsArray.find(c => c.name === results.fireMakingLoser || c.id === results.fireMakingLoser);
      const predLoser = contestantsArray.find(c => c.id === pred.fireMakingLoser);
      return resultLoser?.id === predLoser?.id;
    }
    
    if (predictionType === 'winner') {
      if (!pred.winner || !results.winner) return null;
      const resultWinner = contestantsArray.find(c => c.name === results.winner || c.id === results.winner);
      const predWinner = contestantsArray.find(c => c.id === pred.winner);
      return resultWinner?.id === predWinner?.id;
    }
    
    return null;
  };

  const formatPredictionValue = (value, contestants) => {
    if (!value) return 'N/A';
    
    if (Array.isArray(value)) {
      if (value.length > 0 && value[0] && typeof value[0] === 'object' && 'position' in value[0]) {
        // Double elimination format (array of { contestantId, position }); contestantId may be null
        const sorted = [...value].sort((a, b) => (a.position || 0) - (b.position || 0));
        return sorted.map(item => {
          const name = item.contestantId
            ? normalizeToContestantName(item.contestantId, contestants)
            : '—';
          return `${name} (${votedOutOrdinalLabel(item.position)})`;
        }).join(', ');
      } else {
        // Array of IDs
        return getContestantNamesFromIds(value, contestants).join(', ');
      }
    }
    
    return normalizeToContestantName(value, contestants);
  };

  const formatResultValue = (value, contestants) => {
    if (!value) return 'N/A';
    
    if (Array.isArray(value)) {
      if (value.length > 0 && value[0].position) {
        const sorted = [...value].sort(
          (a, b) => (a.position || 0) - (b.position || 0)
        );
        return sorted.map(item => {
          const name = item.contestantName || normalizeToContestantName(item.contestantId, contestants);
          return `${name} (${votedOutOrdinalLabel(item.position)})`;
        }).join(', ');
      } else {
        // Array format
        return value.map(v => {
          const contestant = contestants.find(c => c.name === v || c.id === v);
          return contestant?.name || v;
        }).join(', ');
      }
    }
    
    const contestant = contestants.find(c => c.name === value || c.id === value);
    return contestant?.name || value;
  };

  /**
   * Ordered multi-elimination weeks: { position: boolean|null } per result slot.
   */
  const getOrderedVotedOutCorrectnessByPosition = (prediction, week) => {
    if (!week?.results?.votedOut || !Array.isArray(week.results.votedOut) || !week.results.votedOut[0]?.position) {
      return null;
    }
    const pred = prediction.predictions || {};
    const predVotedOut = pred.votedOut;
    if (!Array.isArray(predVotedOut)) return null;

    const results = week.results.votedOut;
    const positions = [...new Set(results.map(r => r.position).filter(p => p != null))]
      .sort((a, b) => a - b);
    const byPosition = {};
    positions.forEach(pos => {
      const actualResult = results.find(r => r.position === pos);
      const predItem = predVotedOut.find(p => p && p.position === pos);
      const actualId = actualResult
        ? (actualResult.contestantId || contestants.find(c => c.name === actualResult.contestantName)?.id)
        : null;
      const predId = predItem?.contestantId ?? null;
      if (actualId != null) {
        byPosition[pos] = (predId != null && actualId === predId);
      } else {
        byPosition[pos] = null;
      }
    });
    return byPosition;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-survivor-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading predictions...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  if (displayedPredictions.length === 0) {
    return (
      <div className="text-center py-12">
        <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">
          {restrictToClosedWeeks
            ? "You can't see any predictions until after week 1."
            : 'No predictions submitted yet.'}
        </p>
      </div>
    );
  }

  // Group predictions by week
  const predictionsByWeek = {};
  displayedPredictions.forEach(pred => {
    if (!predictionsByWeek[pred.weekNumber]) {
      predictionsByWeek[pred.weekNumber] = [];
    }
    predictionsByWeek[pred.weekNumber].push(pred);
  });

  const sortedWeeks = Object.keys(predictionsByWeek)
    .map(Number)
    .sort((a, b) => b - a); // Most recent first

  return (
    <div className="space-y-6">
      {showHeader && (
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Prediction History</h2>
          <p className="text-gray-600 mt-1">View your predictions and results</p>
        </div>
      )}

      {sortedWeeks.map(weekNumber => {
        const weekPredictions = predictionsByWeek[weekNumber];
        const week = getWeek(weekNumber);
        const hasResults = week && week.results;
        const isWeekOpen = week?.isOpenForPredictions === true;
        const breakdown = scoreBreakdowns[weekNumber];
        const showResultsState = hasResults && !isWeekOpen;

        return (
          <div key={weekNumber} className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <h3 className="text-xl font-semibold text-gray-900">Week {weekNumber}</h3>
                {showResultsState && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    Results Entered
                  </span>
                )}
              </div>
              {showResultsState && breakdown && (
                <div className="text-right">
                  <div className="text-sm text-gray-500">Points Earned</div>
                  <div className="text-lg font-bold text-survivor-600">+{breakdown.total}</div>
                </div>
              )}
            </div>

            {weekPredictions.map((prediction, idx) => {
              const isCorrect = hasResults ? isPredictionCorrect(prediction, week) : null;
              const pred = prediction.predictions || {};
              const isDoubleEliminationVotedOut =
                Array.isArray(pred.votedOut) &&
                pred.votedOut.length > 0 &&
                pred.votedOut[0] &&
                typeof pred.votedOut[0] === 'object' &&
                'position' in pred.votedOut[0];
              const votedOutCorrectnessByPosition = isDoubleEliminationVotedOut && week
                ? getOrderedVotedOutCorrectnessByPosition(prediction, week)
                : null;

              // For finale weeks, check each prediction individually
              const isVotedOutCorrect = week?.isFinale ? isSpecificPredictionCorrect(prediction, week, 'votedOut') : null;
              const isFireMakingLoserCorrect = week?.isFinale ? isSpecificPredictionCorrect(prediction, week, 'fireMakingLoser') : null;
              const isWinnerCorrect = week?.isFinale ? isSpecificPredictionCorrect(prediction, week, 'winner') : null;

              return (
                <div key={prediction.id || idx} className="border-t border-gray-200 pt-4 mt-4 first:border-t-0 first:pt-0 first:mt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Predictions Column */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Your Predictions</h4>
                      <div className="space-y-2 text-sm">
                        {pred.votedOut && (
                          <div className="flex items-center flex-wrap gap-x-1 gap-y-1">
                            <span className="text-gray-500 mr-2">Voted Out: </span>
                            {isDoubleEliminationVotedOut && votedOutCorrectnessByPosition ? (
                              [...(pred.votedOut || [])]
                                    .sort((a, b) => (a.position || 0) - (b.position || 0))
                                    .map((item, i) => (
                                      <span key={item.position} className="inline-flex items-center gap-1">
                                        {i > 0 && <span className="text-gray-400">, </span>}
                                        <span className="font-medium text-gray-900">
                                          {item.contestantId
                                            ? normalizeToContestantName(item.contestantId, contestants)
                                            : '—'}
                                          {' '}({votedOutOrdinalLabel(item.position)})
                                        </span>
                                        {showResultsState && votedOutCorrectnessByPosition[item.position] !== undefined && votedOutCorrectnessByPosition[item.position] !== null && (
                                          votedOutCorrectnessByPosition[item.position] ? (
                                            <CheckCircleIcon className="h-5 w-5 text-green-500 flex-shrink-0" aria-hidden />
                                          ) : (
                                            <XCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" aria-hidden />
                                          )
                                        )}
                                      </span>
                                    ))
                            ) : (
                              <>
                                <span className="font-medium text-gray-900">
                                  {formatPredictionValue(pred.votedOut, contestants)}
                                </span>
                                {showResultsState && (
                                  week?.isFinale ? (
                                    isVotedOutCorrect !== null && (
                                      isVotedOutCorrect ? (
                                        <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                                      ) : (
                                        <XCircleIcon className="h-5 w-5 text-red-500 ml-2" />
                                      )
                                    )
                                  ) : (
                                    isCorrect !== null && (
                                      isCorrect ? (
                                        <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                                      ) : (
                                        <XCircleIcon className="h-5 w-5 text-red-500 ml-2" />
                                      )
                                    )
                                  )
                                )}
                              </>
                            )}
                          </div>
                        )}
                        {pred.fireMakingLoser && (
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">Fire-Making Loser: </span>
                            <span className="font-medium text-gray-900">
                              {formatPredictionValue(pred.fireMakingLoser, contestants)}
                            </span>
                            {showResultsState && week?.isFinale && isFireMakingLoserCorrect !== null && (
                              isFireMakingLoserCorrect ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                              ) : (
                                <XCircleIcon className="h-5 w-5 text-red-500 ml-2" />
                              )
                            )}
                          </div>
                        )}
                        {pred.winner && (
                          <div className="flex items-center">
                            <span className="text-gray-500 mr-2">Winner: </span>
                            <span className="font-medium text-gray-900">
                              {formatPredictionValue(pred.winner, contestants)}
                            </span>
                            {showResultsState && week?.isFinale && isWinnerCorrect !== null && (
                              isWinnerCorrect ? (
                                <CheckCircleIcon className="h-5 w-5 text-green-500 ml-2" />
                              ) : (
                                <XCircleIcon className="h-5 w-5 text-red-500 ml-2" />
                              )
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Results Column */}
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Actual Results</h4>
                      {showResultsState ? (
                        <div className="space-y-2 text-sm">
                          {week.results.votedOut && (
                            <div>
                              <span className="text-gray-500">Voted Out: </span>
                              <span className="font-medium text-gray-900">
                                {formatResultValue(week.results.votedOut, contestants)}
                              </span>
                            </div>
                          )}
                          {week.results.fireMakingLoser && (
                            <div>
                              <span className="text-gray-500">Fire-Making Loser: </span>
                              <span className="font-medium text-gray-900">
                                {formatResultValue(week.results.fireMakingLoser, contestants)}
                              </span>
                            </div>
                          )}
                          {week.results.winner && (
                            <div>
                              <span className="text-gray-500">Winner: </span>
                              <span className="font-medium text-gray-900">
                                {formatResultValue(week.results.winner, contestants)}
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">Results not yet entered</p>
                      )}
                    </div>
                  </div>

                  {/* Score Breakdown */}
                  {(isWeekOpen || breakdown) && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <div className="text-sm">
                        <span className="text-gray-500">Score Breakdown: </span>
                        <span className="font-medium text-gray-900">
                          {isWeekOpen
                            ? 'No points calculated yet'
                            : formatScoreBreakdown(breakdown)}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Submission Info */}
                  <div className="mt-2 text-xs text-gray-500">
                    Submitted {formatDate(prediction.timestamp)}
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

